import type { Delivery, InventoryLevel, UntrackedMaterial, WasteEntry } from '../interfaces/ILogistics.js';
import type { IMaterialStack } from './board.js';
import { lineNames } from './lines.js';
import { fold } from '../utils/text.js';

/**
 * The Materials segment's unified search — one query filtered against the
 * three panels it congregates (deliveries, inventory, waste).
 *
 * Every match goes through `fold`, the same case/width normalisation every
 * other material lookup on this page uses: 'ｄ16' finds '鉄筋 D16'. A blank
 * query returns the input array untouched (same reference), so an idle search
 * box costs the panels nothing.
 *
 * A comma splits the query into TERMS, OR'd together: "cement, sand" shows
 * both. Typed by hand it reads the way a list reads; it also lets a grouped
 * dashboard alert hand over "show exactly these materials" in one link, which a
 * single substring cannot express. A comma INSIDE double quotes does not split:
 * material names are free text, and the quoted link a tile builds for
 * "Bolts, M12" has to land on that row rather than on two halves. (The
 * alternative, a second URL parameter shadowing the search box, hides from
 * the reader what is filtering their page.)
 *
 * A term in DOUBLE QUOTES matches the whole name instead of any part of it —
 * the usual search convention, and the difference between naming a material and
 * describing one. It matters because material names nest: a plain
 * `Waterproof sheet` also matches `Demo Waterproof Sheet`, so a link meaning
 * "these three rows" would quietly land on four. Alert links quote every name;
 * anything typed by hand stays a substring search unless the reader quotes it.
 */

interface IQueryTerm {
  /** Folded, quotes stripped. */
  text: string;
  /** Match the whole field rather than any part of it. */
  exact: boolean;
}

/** The query split on the commas that sit OUTSIDE double quotes. A quote
 *  never closed is plain text: everything from it on is split on every
 *  comma, as if it were not there, so a stray character cannot swallow the
 *  rest of what the reader typed. */
function splitTerms(query: string): string[] {
  const parts: string[] = [];
  let current = '';
  let quoted = false;
  for (const char of query) {
    if (char === '"') quoted = !quoted;
    if (char === ',' && !quoted) {
      parts.push(current);
      current = '';
      continue;
    }
    current += char;
  }
  // `current` holds the tail — and, when a quote is still open, every comma
  // since it opened.
  return quoted ? [...parts, ...current.split(',')] : [...parts, current];
}

/** The query's non-empty terms, each folded and marked exact-or-not. A
 *  leading '#' is dropped: the cards print refs as "#DEL-300", so a ref read
 *  (or copied) off a card must find it. No material name starts with one. */
function queryTerms(query: string): IQueryTerm[] {
  return splitTerms(query)
    .map((raw) => raw.trim())
    .map((raw) => {
      const quoted = raw.length >= 2 && raw.startsWith('"') && raw.endsWith('"');
      const text = fold(quoted ? raw.slice(1, -1).trim() : raw).replace(/^#/, '');
      return { text, exact: quoted };
    })
    .filter((term) => term.text.length > 0);
}

/** The search box's text as a query: trimmed, so whitespace-only is empty —
 *  a stray space after a paste is no search. */
export function normalizeSearchQuery(raw: string): string {
  return raw.trim();
}

/** Whether a query filters anything — what decides that a search is ACTIVE
 *  (KPIs hidden, "no matches" shown). False for whitespace, and for text with
 *  no term in it (',', '#', '""'), which the filters below ignore too. */
export function hasSearchTerms(raw: string): boolean {
  return queryTerms(raw).length > 0;
}

/** Each field is folded once, then every term is tested against that — not
 *  re-folded per term, per field, per keystroke. */
const matches = (terms: IQueryTerm[], fields: Array<string | null>): boolean =>
  fields.some((field) => {
    if (field === null) return false;
    const folded = fold(field);
    return terms.some((term) => (term.exact ? folded === term.text : folded.includes(term.text)));
  });

/** A delivery's supplier NAME, for a list that prints it (Delivery History) —
 *  the order carries only the supplier's id. */
export type SupplierNameOf = (delivery: Delivery) => string | null;

/** Deliveries match on what a user would scan a card for: any of its
 *  materials, the ref, the destination — and, where the list prints it, the
 *  supplier. A truck carrying three materials is a hit for each of the three —
 *  the card is one record either way. */
export function filterDeliveries(deliveries: Delivery[], query: string, supplierOf?: SupplierNameOf): Delivery[] {
  const terms = queryTerms(query);
  if (terms.length === 0) return deliveries;
  return deliveries.filter((delivery) =>
    matches(terms, [...lineNames(delivery), delivery.ref, delivery.destinationName, supplierOf?.(delivery) ?? null]),
  );
}

/** Per-material stacks (Delivery History's per-material view), narrowed to the
 *  ones the query names. `filterDeliveries` keeps a whole truck when ANY of its
 *  lines matches, and stacking then fans that truck back out into every
 *  material it carried — so "Rebar D16" also showed the Rebar D25 off the same
 *  delivery. A stack matches on its own material, or on a ref, destination or
 *  supplier of its orders. */
export function filterStacks(stacks: IMaterialStack[], query: string, supplierOf?: SupplierNameOf): IMaterialStack[] {
  const terms = queryTerms(query);
  if (terms.length === 0) return stacks;
  return stacks.filter(({ key, face, history }) =>
    matches(terms, [
      key,
      face.destinationName,
      ...[face, ...history].flatMap((delivery) => [delivery.ref, supplierOf?.(delivery) ?? null]),
    ]),
  );
}

/** Tracked rows also match the orders they name — the incoming one, a
 *  never-sent draft, the arrival awaiting a renew — same as an untracked row
 *  matches its delivery ref below. */
export function filterLevels(inventory: InventoryLevel[], query: string): InventoryLevel[] {
  const terms = queryTerms(query);
  if (terms.length === 0) return inventory;
  return inventory.filter((level) =>
    matches(terms, [level.materialName, level.incomingRef, level.draftOrderRef, level.lastArrivalRef]),
  );
}

/** Untracked rows also match on the delivery ref they point at — it is the
 *  row's only identity beyond the name. */
export function filterUntracked(untracked: UntrackedMaterial[], query: string): UntrackedMaterial[] {
  const terms = queryTerms(query);
  if (terms.length === 0) return untracked;
  return untracked.filter((material) => matches(terms, [material.materialName, material.deliveryRef]));
}

/** Waste rows are keyed by material name — the same text the row renders. */
export function filterWaste(waste: WasteEntry[], query: string): WasteEntry[] {
  const terms = queryTerms(query);
  if (terms.length === 0) return waste;
  return waste.filter((entry) => matches(terms, [entry.materialName]));
}
